"use client";

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import UnlockDialog from "./UnlockDialog";
import { GROQButton } from "@/sanity/lib/definitions";
import { STUDENT_AREA_DEMO } from "@/lib/platform-demo/data";
import type {
  DemoCourse,
  DemoHeroSlide,
  DemoMentor,
  DemoWatchItem,
  StudentAreaDemo,
} from "@/lib/platform-demo/types";

// ── Icons ────────────────────────────────────────────────────────────────────
const PlayIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M7 5v14l11-7L7 5z" />
  </svg>
);

const InfoIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const SearchIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
    <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// WCE "rising bars" logo mark (Figma I1845:3217;3506:28527).
const LogoMark = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 14.375 15" fill="none" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.375 1.2356V15H13.0566V4.36376C13.0566 3.63533 12.3989 3.04476 11.588 3.04476C10.777 3.04476 10.1189 3.63533 10.1189 4.36376V15H8.61078V5.62574C8.61078 4.89732 7.95357 4.30675 7.14218 4.30675C6.33078 4.30675 5.67357 4.89732 5.67357 5.62574V15H4.16542V6.88815C4.16542 6.15973 3.50777 5.56916 2.69681 5.56916C1.88586 5.56916 1.22821 6.15973 1.22821 6.88815V15H0V3.95189L14.3737 0L14.375 1.2356Z"
      fill="currentColor"
    />
  </svg>
);

const ChevronDownIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilmIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M8.125 9.375V14.375L12.5 11.875L8.125 9.375Z" fill="#98D8FF" />
    <path d="M16.25 17.5H3.75C3.41859 17.4997 3.10085 17.3678 2.8665 17.1335C2.63216 16.8992 2.50035 16.5814 2.5 16.25V7.5C2.50035 7.16859 2.63216 6.85085 2.8665 6.6165C3.10085 6.38215 3.41859 6.25035 3.75 6.25H16.25C16.5814 6.25035 16.8992 6.38215 17.1335 6.6165C17.3678 6.85085 17.4997 7.16859 17.5 7.5V16.25C17.4997 16.5814 17.3678 16.8992 17.1335 17.1335C16.8992 17.3678 16.5814 17.4997 16.25 17.5ZM3.75 7.5V16.25H16.25V7.5H3.75Z" fill="#98D8FF" />
    <path d="M16.25 3.75H3.75V5H16.25V3.75Z" fill="#98D8FF" />
    <path d="M15 1.25H5V2.5H15V1.25Z" fill="#98D8FF" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path d="M10 18.75C8.26942 18.75 6.57769 18.2368 5.13876 17.2754C3.69983 16.3139 2.57832 14.9473 1.91606 13.3485C1.25379 11.7496 1.08051 9.9903 1.41813 8.29296C1.75575 6.59563 2.58911 5.03653 3.81282 3.81282C5.03653 2.58911 6.59563 1.75575 8.29296 1.41813C9.9903 1.08051 11.7496 1.25379 13.3485 1.91606C14.9473 2.57832 16.3139 3.69983 17.2754 5.13876C18.2368 6.57769 18.75 8.26942 18.75 10C18.75 12.3206 17.8281 14.5462 16.1872 16.1872C14.5462 17.8281 12.3206 18.75 10 18.75ZM10 2.5C8.51664 2.5 7.0666 2.93987 5.83323 3.76398C4.59986 4.58809 3.63856 5.75943 3.07091 7.12988C2.50325 8.50032 2.35473 10.0083 2.64411 11.4632C2.9335 12.918 3.64781 14.2544 4.6967 15.3033C5.7456 16.3522 7.08197 17.0665 8.53683 17.3559C9.99168 17.6453 11.4997 17.4968 12.8701 16.9291C14.2406 16.3614 15.4119 15.4002 16.236 14.1668C17.0601 12.9334 17.5 11.4834 17.5 10C17.5 8.01088 16.7098 6.10323 15.3033 4.6967C13.8968 3.29018 11.9891 2.5 10 2.5Z" fill="#98D8FF" />
    <path d="M12.8688 13.75L9.375 10.2563V4.375H10.625V9.7375L13.75 12.8688L12.8688 13.75Z" fill="#98D8FF" />
  </svg>
);

function initials(name: string): string {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("");
}

// Mouse drag-to-scroll for a horizontal row. Touch/pen keep native scrolling.
// When a drag actually happens it flips `dragGuard` so the click that follows a
// drag is ignored by the item handlers (a drag must never open the popup).
function useDragScroll(dragGuard: MutableRefObject<boolean>) {
  const ref = useRef<HTMLDivElement>(null);
  const st = useRef({ down: false, startX: 0, scroll: 0, moved: false });

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return; // let touch/pen scroll natively
    const el = ref.current;
    if (!el) return;
    st.current = { down: true, startX: e.clientX, scroll: el.scrollLeft, moved: false };
    dragGuard.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const el = ref.current;
    const s = st.current;
    if (!s.down || !el) return;
    const dx = e.clientX - s.startX;
    if (!s.moved && Math.abs(dx) > 5) s.moved = true;
    if (s.moved) {
      dragGuard.current = true;
      el.scrollLeft = s.scroll - dx;
    }
  };
  const end = () => {
    const s = st.current;
    if (!s.down) return;
    s.down = false;
    // Keep the guard up through the click that fires right after this drag,
    // then release it so later plain clicks still work.
    if (s.moved) setTimeout(() => (dragGuard.current = false), 0);
  };

  return {
    ref,
    onPointerDown,
    onPointerMove,
    onPointerUp: end,
    onPointerLeave: end,
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
  };
}

// A horizontally-scrollable row of cards. `data-lenis-prevent` keeps the page's
// smooth-scroll from hijacking it; the scrollbar is hidden for an app-like look.
function Row({
  label,
  children,
  dragGuard,
}: {
  label: string;
  children: React.ReactNode;
  dragGuard: MutableRefObject<boolean>;
}) {
  const drag = useDragScroll(dragGuard);
  return (
    <div className="flex flex-col gap-[2rem]">
      <h3 className="px-[3rem] lg:px-[5rem] m-0 font-geist font-medium text-white text-[2rem] lg:text-[2.5rem]">
        {label}
      </h3>
      <div
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
        onPointerMove={drag.onPointerMove}
        onPointerUp={drag.onPointerUp}
        onPointerLeave={drag.onPointerLeave}
        onDragStart={drag.onDragStart}
        data-lenis-prevent
        className="flex gap-[1.5rem] overflow-x-auto px-[3rem] lg:px-[5rem] pb-[1rem] cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {children}
      </div>
    </div>
  );
}

// ── App top bar (Figma 1845-3217) ──────────────────────────────────────────────
const NAV = [
  "Dashboard",
  "Student Area",
  "Ranks",
  "Leaderboard",
  "Community",
  "Macrodashboard",
  "Rewards",
];
const ACTIVE_NAV = "Student Area";

function TopBar({ onActivate }: { onActivate: () => void }) {
  return (
    <div
      onClick={onActivate}
      className="sticky top-0 z-20 flex items-center justify-between h-[6rem] px-[3rem] lg:px-[5rem] bg-[#0b0b0b]/80 backdrop-blur-[1rem] border-b border-white/10 shadow-[0_0.5rem_1.25rem_rgba(0,0,0,0.24)] cursor-pointer">
      {/* Left — logo + nav */}
      <div className="flex items-center h-full gap-[3rem] lg:gap-[6rem]">
        <div className="flex items-center gap-[1.25rem] text-[#cfcfd0] shrink-0">
          <LogoMark className="w-[2.3rem] h-[2.4rem]" />
          <span className="font-news font-medium text-[1.75rem] tracking-[-0.0225rem] leading-[0.7] pt-[0.5rem] whitespace-nowrap">
            World Class Edge
          </span>
        </div>
        <nav className="hidden lg:flex items-center h-full">
          {NAV.map((item) => {
            const active = item === ACTIVE_NAV;
            return (
              <span
                key={item}
                className={`flex items-center h-full px-[1.5rem] font-geist text-[1.1rem] text-white whitespace-nowrap ${
                  active ? "border-b-[0.2rem] border-[#6abef2]" : "opacity-50"
                }`}>
                {item}
              </span>
            );
          })}
        </nav>
      </div>

      {/* Right — search + user */}
      <div className="flex items-center gap-[2rem] lg:gap-[3rem] shrink-0">
        {/* Search: full box on desktop, icon-only on mobile. */}
        <div className="hidden lg:flex items-center gap-[1rem] rounded-[0.5rem] bg-white/5 px-[1.5rem] h-[3.75rem] w-[18rem]">
          <SearchIcon className="w-[2.5rem] h-[2.5rem] text-white/70" />
          <span className="font-geist font-medium text-[1.25rem] text-white/70 whitespace-nowrap">
            Search for courses
          </span>
        </div>
        <SearchIcon className="lg:hidden w-[1.75rem] h-[1.75rem] text-white/70" />

        <div className="flex items-center gap-[1rem]">
          <span className="hidden lg:inline font-geist font-medium text-[1.25rem] text-white/70 whitespace-nowrap">
            Your Name
          </span>
          <span className="flex items-center rounded-[0.9rem] border border-[#6abef2]/60 p-[0.4rem]">
            <span className="relative block overflow-hidden rounded-[0.5rem] w-[2.5rem] h-[2.5rem]">
              <Image src="/platform-demo/mentor-andrea.jpg" alt="Phillip Krawath" fill className="object-cover" />
            </span>
          </span>
          <ChevronDownIcon className="hidden lg:block w-[2.5rem] h-[2.5rem] text-white/70" />
        </div>
      </div>
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function DemoHero({ slides, onActivate }: { slides: DemoHeroSlide[]; onActivate: () => void }) {
  const [active, setActive] = useState(0);
  const count = slides.length;

  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => setActive((a) => (a + 1) % count), 5000);
    return () => clearInterval(id);
  }, [count]);

  if (count === 0) return null;

  return (
    <div onClick={onActivate} className="relative w-full overflow-hidden h-[30rem] lg:h-[56rem] cursor-pointer">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
            i === active ? "opacity-100" : "opacity-0"
          }`}>
          <Image
            src={slide.image}
            alt=""
            fill
            priority={i === 0}
            sizes="100vw"
            className="object-cover object-right-bottom"
          />
        </div>
      ))}

      {/* Legibility scrims */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-black/90 to-transparent" />

      {/* Copy — bottom-left */}
      <div className="absolute left-0 bottom-0 flex flex-col items-start px-[3rem] lg:px-[5rem] pb-[3.5rem] gap-[2rem] max-w-[52rem]">
        {/* Title artwork (pre-styled SVG). */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={slides[active].titleImage}
          alt=""
          className="h-auto w-[26rem] lg:w-[32rem] object-contain object-left-bottom"
        />
        <p className="hidden lg:block m-0 font-geist text-white/80 text-[1.25rem] lg:text-[1.5rem] leading-[1.4] line-clamp-2">
          {slides[active].subtitle}
        </p>

        <div className="hidden lg:flex items-center gap-[1.5rem]">
          <span className="flex items-center gap-[1rem] rounded-[1rem] bg-white text-black px-[2.25rem] h-[5rem] font-geist font-semibold text-[1.9rem]">
            <PlayIcon className="w-[2.25rem] h-[2.25rem]" />
            Watch now
          </span>
          <span className="flex items-center gap-[1rem] rounded-[1rem] bg-white/15 text-white px-[2.25rem] h-[5rem] font-geist font-semibold text-[1.9rem]">
            <InfoIcon className="w-[2.25rem] h-[2.25rem]" />
            Learn more
          </span>
        </div>

        <div className="flex items-center gap-[1.25rem] text-white/80">
          <FilmIcon className="w-[2.25rem] h-[2.25rem]" />
          <span className="font-geist text-[1.6rem]">{slides[active].videoCount} Videos</span>
          {slides[active].totalDuration && (
            <>
              <span className="w-px h-[1.5rem] bg-white/40" />
              <ClockIcon className="w-[2.25rem] h-[2.25rem]" />
              <span className="font-geist text-[1.6rem]">{slides[active].totalDuration}</span>
            </>
          )}
        </div>
      </div>

      {/* Mentor — bottom-right (desktop) */}
      <div className="hidden lg:flex absolute right-[5rem] bottom-[3.5rem] items-center gap-[1.5rem]">
        {slides[active].mentorPhoto && (
          <span className="relative block overflow-hidden rounded-[1rem] w-[4rem] h-[4rem]">
            <Image src={slides[active].mentorPhoto!} alt={slides[active].mentorName} fill className="object-cover" />
          </span>
        )}
        <span className="font-geist text-white text-[1.85rem]">By {slides[active].mentorName}</span>
      </div>

      {/* Dots */}
      {count > 1 && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-[1.75rem] flex items-center gap-[1rem]">
          {slides.map((s, i) => (
            <span
              key={s.id}
              className={`h-[0.75rem] rounded-full transition-all duration-300 ${
                i === active ? "w-[3rem] bg-white" : "w-[0.75rem] bg-white/40"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Continue watching card ─────────────────────────────────────────────────────
function ContinueCard({ item, onActivate }: { item: DemoWatchItem; onActivate: () => void }) {
  return (
    <div onClick={onActivate} className="shrink-0 flex flex-col gap-[1.25rem] w-[28rem] lg:w-[32rem] cursor-pointer">
      <div className="relative overflow-hidden rounded-[0.75rem] w-full aspect-video bg-white/5">
        <Image src={item.cover} alt={item.title} fill sizes="32rem" className="object-cover" />
        {/* progress bar */}
        <span className="absolute inset-x-0 bottom-0 h-[0.5rem] bg-white/30">
          <span className="block h-full bg-white" style={{ width: `${item.progress}%` }} />
        </span>
        <span className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/30">
          <PlayIcon className="w-[4rem] h-[4rem] text-white" />
        </span>
      </div>
      <div className="flex flex-col gap-[0.4rem] px-[0.25rem]">
        <p className="m-0 font-geist font-medium text-white text-[1.85rem] truncate">{item.title}</p>
        <p className="m-0 font-geist text-white/50 text-[1.55rem] truncate">
          {item.duration ? `${item.lesson} · ${item.duration}` : item.lesson}
        </p>
      </div>
    </div>
  );
}

// ── Masterclass (portrait) card ─────────────────────────────────────────────────
// The `cover` already has the branded title baked into the artwork (same asset
// the platform uses), so we don't overlay `titleImage` here — that would double
// up the title.
function MasterclassCard({ course, onActivate }: { course: DemoCourse; onActivate: () => void }) {
  return (
    <div onClick={onActivate} className="group relative shrink-0 overflow-hidden rounded-[0.75rem] w-[22rem] lg:w-[24rem] h-[32rem] lg:h-[34rem] bg-white/5 cursor-pointer">
      <Image src={course.cover} alt={course.title} fill sizes="24rem" className="object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-[1rem] p-[1.75rem] text-white/70">
        <FilmIcon className="w-[2rem] h-[2rem]" />
        <span className="font-geist text-[1.5rem]">{course.videoCount} Videos</span>
      </div>
    </div>
  );
}

// ── Mentor card ─────────────────────────────────────────────────────────────────
function MentorCard({ mentor, onActivate }: { mentor: DemoMentor; onActivate: () => void }) {
  return (
    <div onClick={onActivate} className="shrink-0 flex flex-col gap-[1.5rem] w-[22rem] lg:w-[24rem] cursor-pointer">
      <div className={`relative overflow-hidden rounded-[1rem] w-full aspect-square bg-gradient-to-br ${mentor.gradient}`}>
        {mentor.photo ? (
          <Image src={mentor.photo} alt={mentor.name} fill sizes="24rem" className="object-cover object-bottom" />
        ) : (
          <span className="flex items-center justify-center w-full h-full font-spacemono text-white/70 text-[3rem]">
            {initials(mentor.name)}
          </span>
        )}
      </div>
      <div className="flex flex-col items-center gap-[0.5rem] text-center">
        <p className="m-0 font-news text-white text-[2.1rem] leading-none tracking-[-0.05rem]">{mentor.name}</p>
        <p className="m-0 font-geist text-white/50 text-[1.55rem]">{mentor.role}</p>
      </div>
    </div>
  );
}

// ── Shell ───────────────────────────────────────────────────────────────────────
interface StudentAreaMockProps {
  unlockHeading?: string;
  unlockBody?: string;
  unlockCtaLabel?: string;
  unlockCta?: GROQButton;
}

/**
 * The scrollable Student Area preview rendered inside the black "screen". The
 * visitor can scroll vertically and drag the rows horizontally; clicking any
 * item (a card, the hero, the top bar) opens the Unlock popup — but a drag never
 * does, so scrolling stays smooth. Nothing inside actually navigates.
 *
 * Renders the hardcoded dummy catalogue instantly (SSR), then upgrades to the
 * live platform catalogue fetched from `/api/platform-demo`. The fetch lives in
 * an API route so the platform's `server-only` client never reaches this (or the
 * Sanity Studio's) browser bundle.
 */
export default function StudentAreaMock({
  unlockHeading,
  unlockBody,
  unlockCtaLabel,
  unlockCta,
}: StudentAreaMockProps) {
  const [open, setOpen] = useState(false);
  const [demo, setDemo] = useState<StudentAreaDemo>(STUDENT_AREA_DEMO);
  // Drives the "Interact" hint — fades out on the first interaction (click,
  // drag or scroll) and stays gone.
  const [hasInteracted, setHasInteracted] = useState(false);
  const markInteracted = useCallback(() => setHasInteracted(true), []);

  // Flipped true by a row drag; `activate` reads it so the click ending a drag
  // doesn't open the popup.
  const dragGuard = useRef(false);
  const activate = useCallback(() => {
    setHasInteracted(true);
    if (dragGuard.current) return;
    setOpen(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/platform-demo")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: StudentAreaDemo | null) => {
        if (!cancelled && data?.courses?.length) setDemo(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <div
        data-lenis-prevent
        onPointerDown={markInteracted}
        onScroll={markInteracted}
        className="absolute inset-0 overflow-y-auto overflow-x-hidden select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <TopBar onActivate={activate} />
        <div className="flex flex-col gap-[4rem] lg:gap-[5rem] pb-[5rem]">
          <DemoHero slides={demo.hero} onActivate={activate} />
          <Row label="Continue watching" dragGuard={dragGuard}>
            {demo.continueWatching.map((item) => (
              <ContinueCard key={item.id} item={item} onActivate={activate} />
            ))}
          </Row>
          <Row label="Masterclasses" dragGuard={dragGuard}>
            {demo.courses.map((course) => (
              <MasterclassCard key={course.id} course={course} onActivate={activate} />
            ))}
          </Row>
          <Row label="Our World Class Trading Mentors" dragGuard={dragGuard}>
            {demo.mentors.map((mentor) => (
              <MentorCard key={mentor.id} mentor={mentor} onActivate={activate} />
            ))}
          </Row>
        </div>
      </div>

      {/* Pulsing scroll indicator — hints vertical scrolling (not clicking) so
          the popup that opens on click doesn't feel like a broken expectation. */}
      <motion.div
        className="absolute top-[15%] left-[20%] -translate-x-1/2 -translate-y-1/2 z-[30] pointer-events-none"
        animate={{ opacity: hasInteracted ? 0 : 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="relative flex items-center justify-center">
          {/* Pulse rings — 3 staggered */}
          {[0, 0.5, 1].map((delay, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: "5rem",
                height: "5rem",
                border: `1.5px solid rgba(169,228,246,${0.5 - i * 0.12})`,
                boxShadow:
                  i === 0 ? "0 0 12px 2px rgba(169,228,246,0.25)" : undefined,
              }}
              animate={{ scale: [1, 2.6 + i * 0.6], opacity: [0.7, 0] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeOut",
                delay,
              }}
            />
          ))}

          {/* Glow backdrop */}
          <div
            className="absolute rounded-full"
            style={{
              width: "5rem",
              height: "5rem",
              background:
                "radial-gradient(circle, rgba(169,228,246,0.18) 0%, transparent 70%)",
              filter: "blur(4px)",
            }}
          />

          {/* Inner dot — drifts vertically to reinforce the scroll direction */}
          <motion.div
            className="relative z-[1] w-[4.5rem] h-[4.5rem] rounded-full flex flex-col items-center justify-center gap-[0.3rem]"
            style={{
              background:
                "radial-gradient(circle at 40% 35%, rgba(169,228,246,0.22), rgba(0,0,0,0.6))",
              border: "1.5px solid rgba(169,228,246,0.55)",
              boxShadow:
                "0 0 18px 4px rgba(169,228,246,0.2), inset 0 0 8px rgba(169,228,246,0.1)",
            }}
            animate={{
              y: [0, 6, 0],
              scale: [1, 1.08, 1],
              boxShadow: [
                "0 0 18px 4px rgba(169,228,246,0.2), inset 0 0 8px rgba(169,228,246,0.1)",
                "0 0 32px 8px rgba(169,228,246,0.45), inset 0 0 14px rgba(169,228,246,0.2)",
                "0 0 18px 4px rgba(169,228,246,0.2), inset 0 0 8px rgba(169,228,246,0.1)",
              ],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            {/* Mouse body with an inner dot that travels down like a scroll wheel */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-[2rem] h-[2.6rem]"
              viewBox="0 0 24 32"
              fill="none"
            >
              <rect
                x="4"
                y="2"
                width="16"
                height="28"
                rx="8"
                stroke="#A9E4F6"
                strokeWidth="1.75"
              />
              <motion.circle
                cx="12"
                r="1.6"
                fill="#A9E4F6"
                animate={{ cy: [8, 14, 8], opacity: [0.4, 1, 0.4] }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </svg>
          </motion.div>

          {/* "Scroll" label */}
          <motion.p
            className="absolute top-[5.5rem] left-1/2 -translate-x-1/2 whitespace-nowrap text-[1.4rem] tracking-[0.15em] uppercase mb-0"
            style={{
              fontFamily: "var(--font-spacemono), monospace",
              background:
                "linear-gradient(90deg, rgba(169,228,246,0.3) 0%, rgba(169,228,246,0.9) 50%, rgba(169,228,246,0.3) 100%)",
              backgroundSize: "200% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
            animate={{ backgroundPosition: ["200% center", "-200% center"] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
          >
            Scroll
          </motion.p>
        </div>
      </motion.div>

      <UnlockDialog
        open={open}
        onOpenChange={setOpen}
        heading={unlockHeading}
        body={unlockBody}
        ctaLabel={unlockCtaLabel}
        cta={unlockCta}
      />
    </>
  );
}
